import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, Download, Calendar, TrendingUp, Zap, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { apiService } from '../services/apiService';

function InformeDiario() {
    const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [mediciones, setMediciones] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(false);

    // Constantes de la planta
    const POTENCIA_IDEAL = 300; // kW ideal cada 15 minutos

    useEffect(() => {
        loadInformeDiario(selectedDate);
    }, [selectedDate]);

    const loadInformeDiario = async (fecha) => {
        setLoading(true);
        try {
            // Obtener el reporte ya calculado desde el servidor
            const intervalos = await apiService.getReporteDiario(fecha);

            setMediciones(intervalos);

            // Calcular resumen del día basado en los intervalos recibidos del servidor
            const totalEnergia = intervalos.reduce((sum, i) => sum + (i.energia || 0), 0);
            const totalSpot = intervalos.reduce((sum, i) => sum + (i.spot || 0), 0);

            // Calcular promedio FP solo de intervalos con datos válidos
            const intervalosConFP = intervalos.filter(i => i.tieneData && parseFloat(i.fp) > 0 && parseFloat(i.fp) <= 1);
            const promedioFP = intervalosConFP.length > 0
                ? intervalosConFP.reduce((sum, i) => sum + parseFloat(i.fp), 0) / intervalosConFP.length
                : 0;

            setResumen({
                fecha: fecha,
                totalMediciones: intervalos.filter(i => i.tieneData).length,
                totalEnergia: totalEnergia.toFixed(2),
                totalSpot: totalSpot.toFixed(2),
                promedioFP: isNaN(promedioFP) ? '0.000' : promedioFP.toFixed(3),
                cumplimiento: ((intervalos.filter(i => i.tieneData).length / 96) * 100).toFixed(1)
            });

        } catch (error) {
            console.error('Error cargando informe:', error);
        }
        setLoading(false);
    };

    const exportarExcel = () => {
        // Crear CSV para descargar
        let csv = 'Intervalo,Hora Inicio,Hora Fin,Potencia (kW),kVA,Factor Potencia,Energia (kWh),Spot (kWh),Mediciones\n';

        mediciones.forEach(m => {
            csv += `${m.numero},${m.horaInicio},${m.horaFin},${m.potencia},${m.kva},${m.fp},${m.energia},${m.spot},${m.cantidadMediciones}\n`;
        });

        // Agregar resumen
        csv += '\n\nRESUMEN DEL DÍA\n';
        csv += `Fecha,${resumen?.fecha}\n`;
        csv += `Total Mediciones,${resumen?.totalMediciones}/96\n`;
        csv += `Energía Total (kWh),${resumen?.totalEnergia}\n`;
        csv += `Spot Total (kWh),${resumen?.totalSpot}\n`;
        csv += `Factor de Potencia Promedio,${resumen?.promedioFP}\n`;
        csv += `Cumplimiento,${resumen?.cumplimiento}%\n`;

        // Descargar
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `informe_generacion_${selectedDate}.csv`;
        link.click();
    };

    return (
        <div className="space-y-6">
            {/* Cabecera con filtros */}
            <div className="card">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center space-x-3">
                        <FileSpreadsheet className="w-8 h-8 text-primary-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Informe Diario de Generación</h2>
                            <p className="text-sm text-gray-500">96 mediciones cada 15 minutos (día inicia a las 00:15)</p>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <Calendar className="w-5 h-5 text-gray-400" />
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                        </div>

                        <button
                            onClick={exportarExcel}
                            disabled={!resumen}
                            className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <Download className="w-5 h-5" />
                            <span>Exportar</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Resumen del día */}
            {resumen && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="card border border-slate-200">
                        <div className="flex items-center space-x-3">
                            <Zap className="w-6 h-6 text-slate-600" />
                            <div>
                                <p className="text-xs text-slate-500 uppercase">Energía Total</p>
                                <p className="text-xl font-bold text-slate-800">{resumen.totalEnergia} kWh</p>
                            </div>
                        </div>
                    </div>

                    <div className="card border border-slate-200">
                        <div className="flex items-center space-x-3">
                            <TrendingUp className="w-6 h-6 text-slate-600" />
                            <div>
                                <p className="text-xs text-slate-500 uppercase">Spot (Exceso)</p>
                                <p className="text-xl font-bold text-slate-800">{resumen.totalSpot} kWh</p>
                            </div>
                        </div>
                    </div>

                    <div className="card border border-slate-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-slate-600 rounded flex items-center justify-center text-white font-bold text-xs">FP</div>
                            <div>
                                <p className="text-xs text-slate-500 uppercase">Factor Potencia</p>
                                <p className="text-xl font-bold text-slate-800">{resumen.promedioFP}</p>
                            </div>
                        </div>
                    </div>

                    <div className="card border border-slate-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-slate-600 rounded flex items-center justify-center text-white font-bold text-xs">#</div>
                            <div>
                                <p className="text-xs text-slate-500 uppercase">Mediciones</p>
                                <p className="text-xl font-bold text-slate-800">{resumen.totalMediciones}/96</p>
                            </div>
                        </div>
                    </div>

                    <div className={`card border ${parseFloat(resumen.cumplimiento) >= 95 ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
                        <div className="flex items-center space-x-3">
                            {parseFloat(resumen.cumplimiento) >= 95 ? (
                                <div className="w-6 h-6 bg-emerald-600 rounded flex items-center justify-center text-white font-bold text-xs">OK</div>
                            ) : (
                                <AlertTriangle className="w-6 h-6 text-red-600" />
                            )}
                            <div>
                                <p className={`text-xs uppercase ${parseFloat(resumen.cumplimiento) >= 95 ? 'text-emerald-600' : 'text-red-600'}`}>Cumplimiento</p>
                                <p className={`text-xl font-bold ${parseFloat(resumen.cumplimiento) >= 95 ? 'text-emerald-700' : 'text-red-700'}`}>{resumen.cumplimiento}%</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabla de intervalos */}
            <div className="card overflow-hidden">
                <h3 className="text-lg font-semibold mb-4">Detalle por Intervalo (cada 15 minutos)</h3>

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto max-h-96">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hora</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Potencia (kW)</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">kVA</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">FP</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Energía (kWh)</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spot (kWh)</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {(() => {
                                    if (!mediciones) return null;

                                    // Solo mostrar intervalos pasados y el actual (evitar futuros vacíos)
                                    // Ordenar de más reciente (arriba) a más antiguo (abajo)
                                    const displayList = mediciones.filter(m => !m.esFuturo).reverse();

                                    return displayList.map((m) => (
                                        <tr key={m.numero} className={m.esActual ? 'bg-blue-100 border-l-4 border-blue-500' : !m.tieneData ? 'bg-red-50' : m.spot > 0 ? 'bg-purple-50' : ''}>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{m.numero}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{m.horaInicio} - {m.horaFin}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-right font-mono">{m.potencia}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-right font-mono">{m.kva}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-right font-mono">{m.fp}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-right font-mono font-semibold">{m.energia}</td>
                                            <td className={`px-4 py-2 whitespace-nowrap text-sm text-right font-mono font-semibold ${m.spot > 0 ? 'text-purple-600' : ''}`}>
                                                {m.spot > 0 ? m.spot : '-'}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-center">
                                                {m.esActual ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                        EN CURSO
                                                    </span>
                                                ) : m.tieneData ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                        OK ({m.cantidadMediciones})
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                        Sin datos
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ));
                                })()}
                            </tbody>
                            <tfoot className="bg-gray-100 font-bold sticky bottom-0 border-t-2 border-gray-300">
                                {(() => {
                                    if (!mediciones || mediciones.length === 0) return null;

                                    const rawList = mediciones.filter(m => m.tieneData);

                                    // Agrupar por hora para seguir la lógica solicitada (promedio de 4 por hora, sumar 24 horas)
                                    const porHora = {};
                                    mediciones.forEach(m => {
                                        const hr = m.horaInicio.split(':')[0];
                                        if (!porHora[hr]) porHora[hr] = { sumPotencia: 0, sumKva: 0, sumFp: 0, count: 0, sumEnergia: 0, sumSpot: 0 };
                                        if (m.tieneData) {
                                            porHora[hr].sumPotencia += parseFloat(m.potencia);
                                            porHora[hr].sumKva += parseFloat(m.kva);
                                            porHora[hr].sumFp += parseFloat(m.fp);
                                            porHora[hr].sumEnergia += m.energia;
                                            porHora[hr].sumSpot += m.spot;
                                            porHora[hr].count++;
                                        }
                                    });

                                    let totalPotenciaHr = 0;
                                    let totalKvaHr = 0;
                                    let totalEnergiaHr = 0;
                                    let totalSpotHr = 0;
                                    let horasConData = 0;

                                    Object.keys(porHora).forEach(hr => {
                                        const h = porHora[hr];
                                        if (h.count > 0) {
                                            totalPotenciaHr += (h.sumPotencia / h.count);
                                            totalKvaHr += (h.sumKva / h.count);
                                            totalEnergiaHr += h.sumEnergia;
                                            totalSpotHr += h.sumSpot;
                                            horasConData++;
                                        }
                                    });

                                    const totalMediciones = rawList.reduce((sum, m) => sum + parseInt(m.cantidadMediciones || 0), 0);

                                    return (
                                        <tr>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">TOT</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">TOTAL DIARIO (Sum 24h)</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-mono text-indigo-700">{totalPotenciaHr.toFixed(1)} kW</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-mono text-indigo-600">{totalKvaHr.toFixed(1)} kVA</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-mono">{resumen?.promedioFP}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-mono text-blue-700 font-bold">{totalEnergiaHr.toFixed(2)} kWh</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-mono text-purple-700">{totalSpotHr.toFixed(2)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center text-xs text-gray-600">
                                                {totalMediciones} pts
                                            </td>
                                        </tr>
                                    );
                                })()}
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default InformeDiario;
